<template>
  <div>
    <v-container class="page-title page-title--advanced">
      <v-row>
        <v-col class="page-title__title-box">
          <h4 class="page-title__title">
            {{ $t("heading.instance.advanced.wordpressMcp.title") }}
          </h4>
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-row>
        <v-col cols="12" class="pb-2">
          <auto-updates-card-skeleton v-if="fetching" />

          <v-card v-else elevation="4">
            <v-card-text
              class="d-flex justify-space-between text--gray-darken2"
            >
              <div>
                <h6>
                  {{ $t("heading.instance.advanced.wordpressMcp.title") }}
                </h6>
                <p class="mb-0 mt-1">
                  {{ $t("heading.instance.advanced.wordpressMcp.description") }}
                </p>
                <alert-sm
                  v-if="!isOn && state !== 'failed'"
                  :class="'alert-sm--primary mt-3'"
                  :text="
                    $t(
                      'heading.instance.advanced.wordpressMcp.pluginRequiredHint'
                    )
                  "
                />
              </div>
              <div class="d-flex align-center">
                <ButtonSwitch
                  class="mt-0"
                  :loading="isBusy"
                  :disabled="isBusy"
                  :value="isOn"
                  @click.native.stop.prevent="toggle"
                />
              </div>
            </v-card-text>

            <template v-if="state === 'failed'">
              <v-divider />
              <v-card-text class="py-3 d-flex align-center">
                <v-icon color="error" size="18" class="mr-2"
                  >$alertcircle</v-icon
                >
                <span class="error--text p-sm">{{
                  $t("heading.instance.advanced.wordpressMcp.failed")
                }}</span>
              </v-card-text>
            </template>
          </v-card>
        </v-col>

        <v-col
          v-if="state === 'active' || state === 'endpoint_unavailable'"
          cols="12"
          class="pb-2"
        >
          <v-card elevation="4">
            <v-card-text class="text--gray-darken2">
              <h6 :class="documentationUrl ? 'mb-2' : 'mb-4'">
                {{ $t("heading.instance.advanced.wordpressMcp.howToConnect") }}
              </h6>
              <p v-if="documentationUrl" class="mb-4">
                <a
                  :href="documentationUrl"
                  target="_blank"
                  rel="noopener"
                  class="info--text"
                >{{
                  $t(
                    "heading.instance.advanced.wordpressMcp.documentationLink"
                  )
                }}</a>
              </p>

              <div class="step">
                <div class="step__num">1</div>
                <div class="step__body">
                  <div class="step__title">
                    {{
                      $t("heading.instance.advanced.wordpressMcp.step1Title")
                    }}
                  </div>
                  <p
                    class="mb-0 mt-1"
                    v-html="
                      $t('heading.instance.advanced.wordpressMcp.step1Body')
                    "
                  ></p>
                </div>
                <div class="step__action">
                  <v-btn
                    elevation="0"
                    block
                    class="font-weight-bold v-btn--default"
                    :loading="wpAdminLoading"
                    :disabled="wpAdminLoading"
                    @click="openWpAdmin"
                  >
                    {{ $t("heading.instance.advanced.wordpressMcp.step1Link") }}
                  </v-btn>
                </div>
              </div>

              <v-divider class="my-4" />

              <div class="step">
                <div class="step__num">2</div>
                <div class="step__body">
                  <div class="step__title">
                    {{
                      $t("heading.instance.advanced.wordpressMcp.step2Title")
                    }}
                  </div>
                  <div
                    class="well well--gray d-flex align-center py-2 px-4 mt-2"
                  >
                    <code class="mcp-code flex-grow-1">{{ endpoint }}</code>
                    <copy-text-button :text="endpoint" color="info" size="16" />
                  </div>
                </div>
              </div>

              <v-divider class="my-4" />

              <div class="step">
                <div class="step__num">3</div>
                <div class="step__body">
                  <div class="step__title">
                    {{
                      $t("heading.instance.advanced.wordpressMcp.step3Title")
                    }}
                  </div>
                  <p
                    class="mb-0 mt-1"
                    v-html="
                      $t('heading.instance.advanced.wordpressMcp.step3Body')
                    "
                  ></p>
                  <div class="well well--gray py-2 px-4 mt-2">
                    <div class="d-flex align-start">
                      <pre class="mcp-code flex-grow-1 mb-0">{{
                        configJson
                      }}</pre>
                      <copy-text-custom-button :text="configJson" class="copy-button">
                        <v-btn
                          elevation="0"
                          class="ml-auto v-btn--default font-weight-bold"
                        >{{
                          $t("heading.instance.advanced.wordpressMcp.copy")
                        }}</v-btn>
                      </copy-text-custom-button>
                    </div>
                  </div>
                </div>
              </div>

              <template v-if="state === 'endpoint_unavailable'">
                <v-divider class="my-4" />
                <div class="d-flex align-center warning--text">
                  <v-icon color="warning" size="18" class="mr-2"
                    >$alertcircle</v-icon
                  >
                  <span>{{
                    $t(
                      "heading.instance.advanced.wordpressMcp.endpointUnavailable"
                    )
                  }}</span>
                </div>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog
      :persistent="modalOptions.persistent"
      v-model="modalOptions.open"
      ref="dialog"
      transition="custom-dialog-transition"
    >
      <div
        class="card-overlay"
        v-if="!modalOptions.persistent"
        @click="modalOptions.open = !modalOptions.open"
      />
      <div class="card-overlay" v-else @click="$refs.dialog.animateClick()" />
      <basic-modal
        style="width: 560px"
        :modalOptions="modalOptions"
        @modal-close="modalClose"
        :key="modalRender"
      />
    </v-dialog>
  </div>
</template>

<script>
import Api from "@/apis/Api";
import ActionModalMixin from "../../../mixins/ActionModalMixin";
import AlertSm from "../../../components/alerts/AlertSm.vue";
import AutoUpdatesCardSkeleton from "../../../components/cards/AutoUpdatesCardSkeleton.vue";
import ButtonSwitch from "../../../components/buttons/ButtonSwitch.vue";
import BasicModal from "../../../components/modal/BasicModal.vue";
import CopyTextButton from "../../../components/buttons/CopyTextButton.vue";
import CopyTextCustomButton from "../../../components/buttons/CopyTextCustomButton.vue";

export default {
  name: "WordpressMcp",
  components: {
    AlertSm,
    AutoUpdatesCardSkeleton,
    ButtonSwitch,
    BasicModal,
    CopyTextButton,
    CopyTextCustomButton,
  },
  mixins: [ActionModalMixin],
  props: {
    instance: Object,
  },
  data() {
    return {
      applicationId: this.$route.params.id,
      fetching: true,
      submitting: false,
      awaitingWebsocketUpdate: false,
      pendingMcpAction: null,
      pollTimer: null,
      state: "disabled",
      endpoint: null,
      configJson: null,
      wpAdminLoading: false,
    };
  },
  computed: {
    documentationUrl() {
      return this.$store.state.home?.app?.documentation_link || "";
    },
    isOn() {
      return (
        this.state === "active" ||
        this.state === "installing" ||
        this.state === "removing" ||
        this.state === "endpoint_unavailable"
      );
    },
    isBusy() {
      return (
        this.state === "installing" ||
        this.state === "removing" ||
        this.submitting ||
        this.awaitingWebsocketUpdate
      );
    },
  },
  mounted() {
    this.fetchStatus();
    this.$root.$on("wordpress-mcp-status-changed", this.onMcpStatusChanged);
  },
  beforeDestroy() {
    this.$root.$off("wordpress-mcp-status-changed", this.onMcpStatusChanged);
    this.stopPolling();
  },
  methods: {
    async fetchStatus() {
      try {
        const { data } = await Api.get(
          `/instances/${this.applicationId}/wordpress/mcp`
        );
        this.applyStatus(data.data);
        this.maybePoll();
      } catch (error) {
        this.stopPolling();
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });
      } finally {
        this.fetching = false;
      }
    },
    applyStatus(payload) {
      const incoming = payload.state;

      if (this.pendingMcpAction === "enable" && incoming === "disabled") {
        this.state = "installing";
        this.endpoint = payload.endpoint;
        this.configJson = payload.config_json;
        return;
      }

      if (
        this.pendingMcpAction === "disable" &&
        (incoming === "active" || incoming === "endpoint_unavailable")
      ) {
        this.state = "removing";
        this.endpoint = payload.endpoint;
        this.configJson = payload.config_json;
        return;
      }

      this.state = incoming;
      this.endpoint = payload.endpoint;
      this.configJson = payload.config_json;
    },
    shouldKeepPolling() {
      if (!this.pendingMcpAction) {
        return this.state === "installing" || this.state === "removing";
      }

      return this.awaitingWebsocketUpdate || !this.isTerminalStateForPendingAction();
    },
    isTerminalStateForPendingAction() {
      if (this.pendingMcpAction === "enable") {
        return ["active", "endpoint_unavailable", "failed"].includes(this.state);
      }

      if (this.pendingMcpAction === "disable") {
        return ["disabled", "failed"].includes(this.state);
      }

      return false;
    },
    completePendingAction() {
      if (!this.pendingMcpAction) {
        return;
      }

      const action = this.pendingMcpAction;
      this.pendingMcpAction = null;
      this.awaitingWebsocketUpdate = false;
      this.stopPolling();
      this.notifyMcpActionResult(action);
    },
    notifyMcpActionResult(action) {
      if (action === "enable") {
        if (this.state === "active" || this.state === "endpoint_unavailable") {
          this.$store.dispatch("addAlert", {
            success: true,
            successMessage: this.$t(
              "heading.instance.advanced.wordpressMcp.enableSuccess"
            ),
          });
          return;
        }

        this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: this.$t("heading.instance.advanced.wordpressMcp.failed"),
        });
        return;
      }

      if (action === "disable") {
        if (this.state === "disabled") {
          this.$store.dispatch("addAlert", {
            success: true,
            successMessage: this.$t(
              "heading.instance.advanced.wordpressMcp.disableSuccess"
            ),
          });
          return;
        }

        this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: this.$t("heading.instance.advanced.wordpressMcp.failed"),
        });
      }
    },
    toggle() {
      if (this.isBusy) {
        return;
      }
      if (this.isOn) {
        this.openDisableConfirm();
      } else {
        this.enable();
      }
    },
    openDisableConfirm() {
      this.resetModal();
      this.modalOptions.title = this.$t(
        "heading.instance.advanced.wordpressMcp.disableConfirmTitle"
      );
      this.modalOptions.message = this.$t(
        "heading.instance.advanced.wordpressMcp.disableConfirmBody"
      );
      this.modalOptions.icon = "$alertwarning";
      this.modalOptions.color = "warning";
      this.modalOptions.persistent = true;
      this.modalOptions.buttons.unshift({
        label: this.$t("heading.instance.advanced.wordpressMcp.confirm"),
        color: "warning",
        onclick: () => {
          this.modalClose();
          this.disable();
        },
      });
      this.modalOptions.open = true;
    },
    async enable() {
      this.submitting = true;
      this.pendingMcpAction = "enable";
      try {
        await Api.post(`/instances/${this.applicationId}/wordpress/mcp`);
        if (this.pendingMcpAction === "enable") {
          this.state = "installing";
          this.awaitingWebsocketUpdate = true;
          this.maybePoll();
        }
      } catch (error) {
        this.pendingMcpAction = null;
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });
      } finally {
        this.submitting = false;
      }
    },
    async disable() {
      this.submitting = true;
      this.pendingMcpAction = "disable";
      try {
        await Api.delete(`/instances/${this.applicationId}/wordpress/mcp`);
        if (this.pendingMcpAction === "disable") {
          this.state = "removing";
          this.awaitingWebsocketUpdate = true;
          this.maybePoll();
        }
      } catch (error) {
        this.pendingMcpAction = null;
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });
      } finally {
        this.submitting = false;
      }
    },
    async onMcpStatusChanged(instance) {
      if (String(instance.id) !== String(this.applicationId)) {
        return;
      }

      if (!this.pendingMcpAction) {
        await this.fetchStatus();
        return;
      }

      try {
        const { data } = await Api.get(
          `/instances/${this.applicationId}/wordpress/mcp`
        );
        this.applyStatus(data.data);
      } catch (error) {
        this.pendingMcpAction = null;
        this.awaitingWebsocketUpdate = false;
        this.stopPolling();
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });
        return;
      }

      this.completePendingAction();
    },
    maybePoll() {
      this.stopPolling();
      if (!this.shouldKeepPolling()) {
        return;
      }

      this.pollTimer = setInterval(async () => {
        try {
          const { data } = await Api.get(
            `/instances/${this.applicationId}/wordpress/mcp`
          );
          this.applyStatus(data.data);

          if (this.isTerminalStateForPendingAction()) {
            this.completePendingAction();
            return;
          }

          if (!this.shouldKeepPolling()) {
            this.awaitingWebsocketUpdate = false;
            this.stopPolling();
          }
        } catch (error) {
          this.stopPolling();
        }
      }, 3000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async openWpAdmin() {
      this.wpAdminLoading = true;
      try {
        const { data } = await Api.post(
          `/instances/${this.applicationId}/sso-url`,
          { user: 1 }
        );
        const ssoUrl = data.data.sso_url;
        const newWindow = window.open(ssoUrl, "_blank");
        if (
          !newWindow ||
          newWindow.closed ||
          typeof newWindow.closed === "undefined"
        ) {
          window.location.href = ssoUrl;
        }
      } catch (error) {
        this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });
      } finally {
        this.wpAdminLoading = false;
      }
    },
  },
  watch: {
    "modalOptions.open": function (value) {
      value
        ? this.$store.dispatch("lockBodyScroll")
        : this.$store.dispatch("unlockBodyScroll");
    },
  },
};
</script>

<style lang="scss" scoped>
.step {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.step__num {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 50%;
  background: map-get($primary, lighten4);
  color: map-get($primary, darken1);
  font-weight: 700;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step__title {
  font-weight: 600;
  font-size: 15px;
  color: map-get($text, headings);
}

.step__body {
  flex: 1;
  min-width: 0;
}

.step__action {
  flex-shrink: 0;
  min-width: 180px;
}

.step__body ::v-deep .mcp-path {
  font-family: ui-monospace, Menlo, monospace;
  background: map-get($gray, lighten6);
  border: 1px solid map-get($gray, lighten3);
  border-radius: 6px;
  padding: 2px 7px;
  font-size: 12.5px;
  color: map-get($gray, darken1);
}

.mcp-code {
  font-family: ui-monospace, Menlo, monospace;
  font-size: 13px;
  color: inherit;
  background: transparent;
  white-space: pre-wrap;
  word-break: break-all;
}

.copy-button {
  flex-shrink: 0;
}

@media (max-width: 767px) {
  .step {
    flex-direction: column;
    gap: 8px;
  }

  .step__action {
    align-self: stretch;
    min-width: 0;
    width: 100%;
  }
}
</style>

<style lang="scss">
body.is-dark {
  .step__body .mcp-path {
    background-color: #343446;
    border-color: #404055;
    color: #c5c3d3;
  }

  .step__num {
    background-color: rgba(0, 200, 83, 0.12);
    color: #00c853;
  }
}
</style>
